/* GENERATED FILE — DO NOT EDIT.
 *
 * W125 — the exact estimated 1RM, emitted from the one definition the app compiles:
 *   HybridTrainer/Services/OneRepMax.swift
 *
 * Regenerate:  KATA_EMIT_WEB_TOKENS=1 ./tools/engine-tests/run.sh
 * The harness gate `w125-the-1rm` regenerates this in memory on every run and fails if it differs;
 * `oneRepMax.test.ts` holds every function below to the Swift's own answers (`SWIFT_PROBES`).
 *
 * A set feeds only with 1…10 reps; reps in reserve = 10 − RPE, clamped 0…5 (none or 0 = 0);
 * e1RM = load × (1 + (reps + RIR) / 30); a single at full effort (reps + RIR = 1) = the load.
 * When the body is part of the load, load = kg + the weigh-in
 * nearest the set's day (assisted: bodyweight − assist; a set the record says was banded, with no kg: nothing; reps and
 * no kg from a source that cannot tell: bodyweight) and the figure is the ADDED load.
 */

export const ONE_RM_DIVISOR = 30;
export const ONE_RM_MAX_REPS = 10;
export const ONE_RM_RPE_SCALE = 10;
export const ONE_RM_MAX_RIR = 5;
export const BODY_LOAD_EQUIPMENT: readonly string[] = ["pull_up_bar", "rings", "dip_bars", "parallettes", "dip_belt", "weight_vest"];
export const BODY_LOAD_VETO_EQUIPMENT: readonly string[] = ["resistance_band"];
export const BODY_IS_LOAD_HINT = "For pull-ups, dips and the like — the estimate counts bodyweight. Not for band-assisted versions.";
export const NO_BODYWEIGHT_ATHLETE = "Add your bodyweight to see this";
export const NO_BODYWEIGHT_COACH = "No bodyweight logged";

/** `OneRepMax.Rule`: an engine exercise keeps today's chart; a coach's carries the switch. */
export type OneRMRule = { readonly kind: "engine" } | { readonly kind: "coach"; readonly enabled: boolean; readonly bodyIsLoad: boolean };
export const ENGINE_RULE: OneRMRule = { kind: "engine" };
export function ruleBodyIsLoad(r: OneRMRule): boolean { return r.kind === "coach" && r.enabled && r.bodyIsLoad; }

/** One logged set. `band` null = the source cannot tell (W125-F3: a bodyweight set when the body is load); "" = not banded. */
export type OneRMSet = {
  readonly reps: number | null;
  readonly weightKg: number | null;
  readonly rpe: number | null;
  readonly assistKg: number | null;
  readonly band: string | null;
};
export type WeighIn = { readonly at: number; readonly kg: number };

export function rir(rpe: number | null): number {
  if (rpe === null || !(rpe > 0)) return 0;
  return Math.min(ONE_RM_MAX_RIR, Math.max(0, ONE_RM_RPE_SCALE - rpe));
}

export function feeds(s: OneRMSet, bodyIsLoad: boolean): boolean {
  const r = s.reps;
  if (r === null || r < 1 || r > ONE_RM_MAX_REPS) return false;
  const w = s.weightKg ?? 0;
  if (!bodyIsLoad) return w > 0;
  if (w > 0) return true;
  if (s.assistKg !== null && s.assistKg > 0) return true;
  if (s.band === null) return true;
  return s.band.trim() === "";
}

/** The DISPLAYED figure: the added load when the body is part of it, else the e1RM; null when it feeds nothing. */
export function estimate(s: OneRMSet, bodyweightKg: number | null, bodyIsLoad: boolean): number | null {
  if (!feeds(s, bodyIsLoad) || s.reps === null) return null;
  const w = s.weightKg ?? 0;
  const reps = s.reps + rir(s.rpe);
  const factor = reps <= 1 ? 1 : 1 + reps / ONE_RM_DIVISOR;
  if (!bodyIsLoad) return w * factor;
  if (bodyweightKg === null || !(bodyweightKg > 0)) return null;
  const load = w > 0 ? w + bodyweightKg : bodyweightKg - (s.assistKg ?? 0);
  if (!(load > 0)) return 0;
  return Math.max(0, load * factor - bodyweightKg);
}

const startOfDay = (ms: number): number => { const d = new Date(ms); d.setHours(0, 0, 0, 0); return d.getTime(); };

/** The weigh-in nearest the day (by calendar day); a tie goes to the EARLIER; null when there is none. */
export function bodyweightOn(day: number, weighIns: readonly WeighIn[]): number | null {
  const target = startOfDay(day);
  let best: { dist: number; at: number; kg: number } | null = null;
  for (const w of weighIns) {
    if (!(w.kg > 0)) continue;
    const dist = Math.abs(Math.round((startOfDay(w.at) - target) / 86_400_000));
    if (best === null || dist < best.dist || (dist === best.dist && w.at < best.at)) best = { dist, at: w.at, kg: w.kg };
  }
  return best === null ? null : best.kg;
}

/** The best displayed figure of one day's sets under the rule. */
export function bestOf(sets: readonly OneRMSet[], day: number, rule: OneRMRule, weighIns: readonly WeighIn[]): number | null {
  const body = ruleBodyIsLoad(rule);
  const bw = body ? bodyweightOn(day, weighIns) : null;
  let best: number | null = null;
  for (const s of sets) { const v = estimate(s, bw, body); if (v !== null && (best === null || v > best)) best = v; }
  return best;
}

export function suggestsBodyIsLoad(equipment: readonly string[]): boolean {
  return equipment.some((e) => BODY_LOAD_EQUIPMENT.includes(e)) && !equipment.some((e) => BODY_LOAD_VETO_EQUIPMENT.includes(e));
}

/** Swift's own answers — `oneRepMax.test.ts` holds `estimate` to them. */
export const SWIFT_PROBES: readonly { readonly name: string; readonly set: OneRMSet; readonly bodyweightKg: number | null; readonly bodyIsLoad: boolean; readonly feeds: boolean; readonly estimate: number | null }[] = [
  { name: "plain barbell 5 @ 100", set: { reps: 5, weightKg: 100, rpe: null, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: true, estimate: 116.6666667 },
  { name: "RPE 8 = 2 in reserve", set: { reps: 5, weightKg: 100, rpe: 8, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: true, estimate: 123.3333333 },
  { name: "RPE 3 clamps to 5 in reserve", set: { reps: 5, weightKg: 100, rpe: 3, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: true, estimate: 133.3333333 },
  { name: "RPE 0 is not rated", set: { reps: 5, weightKg: 100, rpe: 0, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: true, estimate: 116.6666667 },
  { name: "RPE 10.5 clamps to 0", set: { reps: 3, weightKg: 100, rpe: 10.5, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: true, estimate: 110 },
  { name: "10 reps feeds", set: { reps: 10, weightKg: 60, rpe: null, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: true, estimate: 80 },
  { name: "11 reps feeds nothing", set: { reps: 11, weightKg: 60, rpe: null, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: false, estimate: null },
  { name: "0 reps feeds nothing", set: { reps: 0, weightKg: 60, rpe: null, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: false, estimate: null },
  { name: "no load, body not load", set: { reps: 5, weightKg: null, rpe: null, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: false, estimate: null },
  { name: "weighted pull-up +32.5 x5 @ 74", set: { reps: 5, weightKg: 32.5, rpe: null, assistKg: null, band: "" }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 50.25 },
  { name: "weighted pull-up, no weigh-in", set: { reps: 5, weightKg: 32.5, rpe: null, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: true, feeds: true, estimate: null },
  { name: "bodyweight-only pull-up x8 @ 74", set: { reps: 8, weightKg: null, rpe: null, assistKg: null, band: "" }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 19.73333333 },
  { name: "bodyweight-only, weight 0", set: { reps: 8, weightKg: 0, rpe: null, assistKg: null, band: "" }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 19.73333333 },
  { name: "assisted 20 kg x6 @ 74", set: { reps: 6, weightKg: null, rpe: null, assistKg: 20, band: "" }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 0 },
  { name: "assisted heavier than the body", set: { reps: 6, weightKg: null, rpe: null, assistKg: 90, band: "" }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 0 },
  { name: "banded, no kg", set: { reps: 6, weightKg: null, rpe: null, assistKg: null, band: "M" }, bodyweightKg: 74, bodyIsLoad: true, feeds: false, estimate: null },
  { name: "banded, with assist kg", set: { reps: 6, weightKg: null, rpe: null, assistKg: 15, band: "M" }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 0 },
  { name: "source cannot tell the band, no kg", set: { reps: 6, weightKg: null, rpe: null, assistKg: null, band: null }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 14.8 },
  { name: "source cannot tell the band, weighted", set: { reps: 6, weightKg: 10, rpe: null, assistKg: null, band: null }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 26.8 },
  { name: "F3 server set, no kg, body ON, weigh-in", set: { reps: 8, weightKg: null, rpe: null, assistKg: null, band: null }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 19.73333333 },
  { name: "F3 server set, no kg, body ON, no weigh-in", set: { reps: 8, weightKg: null, rpe: null, assistKg: null, band: null }, bodyweightKg: null, bodyIsLoad: true, feeds: true, estimate: null },
  { name: "F3 server set, no kg, body OFF", set: { reps: 8, weightKg: null, rpe: null, assistKg: null, band: null }, bodyweightKg: 74, bodyIsLoad: false, feeds: false, estimate: null },
  { name: "F3 device set, no kg, body ON, weigh-in", set: { reps: 8, weightKg: null, rpe: null, assistKg: null, band: "" }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 19.73333333 },
  { name: "body as load, 11 reps", set: { reps: 11, weightKg: 10, rpe: null, assistKg: null, band: "" }, bodyweightKg: 74, bodyIsLoad: true, feeds: false, estimate: null },
  { name: "body as load with RPE 9", set: { reps: 5, weightKg: 20, rpe: 9, assistKg: null, band: "" }, bodyweightKg: 80, bodyIsLoad: true, feeds: true, estimate: 40 },
  { name: "added load would be negative → 0", set: { reps: 1, weightKg: null, rpe: null, assistKg: null, band: "" }, bodyweightKg: 74, bodyIsLoad: true, feeds: true, estimate: 0 },
  { name: "a single at full effort is the lift", set: { reps: 1, weightKg: 140, rpe: null, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: true, estimate: 140 },
  { name: "a single at RPE 9 = 2 reps' worth", set: { reps: 1, weightKg: 140, rpe: 9, assistKg: null, band: "" }, bodyweightKg: null, bodyIsLoad: false, feeds: true, estimate: 149.3333333 },
];

/** Swift's own answers on the pre-suggestion (W125-F1: a band row is never pre-suggested). */
export const SWIFT_SUGGEST_PROBES: readonly { readonly equipment: readonly string[]; readonly suggests: boolean }[] = [
  { equipment: ["pull_up_bar"], suggests: true },
  { equipment: ["rings"], suggests: true },
  { equipment: ["dip_bars"], suggests: true },
  { equipment: ["parallettes"], suggests: true },
  { equipment: ["dip_belt"], suggests: true },
  { equipment: ["weight_vest"], suggests: true },
  { equipment: ["pull_up_bar", "resistance_band"], suggests: false },
  { equipment: ["rings", "resistance_band"], suggests: false },
  { equipment: ["dip_bars", "resistance_band"], suggests: false },
  { equipment: ["parallettes", "resistance_band"], suggests: false },
  { equipment: ["dip_belt", "resistance_band"], suggests: false },
  { equipment: ["weight_vest", "resistance_band"], suggests: false },
  { equipment: ["resistance_band"], suggests: false },
  { equipment: ["barbell", "squat_rack"], suggests: false },
  { equipment: [], suggests: false },
  { equipment: ["dip_belt", "pull_up_bar", "resistance_band"], suggests: false },
];

/** Swift's own answers on the nearest weigh-in (days are noon UTC). */
export const SWIFT_WEIGH_IN_PROBES: readonly { readonly name: string; readonly day: number; readonly weighIns: readonly WeighIn[]; readonly kg: number | null }[] = [
  { name: "none", day: 1789992000000, weighIns: [], kg: null },
  { name: "same day", day: 1789992000000, weighIns: [{ at: 1789992000000, kg: 74 }], kg: 74 },
  { name: "nearest after", day: 1789992000000, weighIns: [{ at: 1789560000000, kg: 70 }, { at: 1790164800000, kg: 74 }], kg: 74 },
  { name: "nearest before", day: 1789992000000, weighIns: [{ at: 1789905600000, kg: 72 }, { at: 1790251200000, kg: 75 }], kg: 72 },
  { name: "tie goes to the earlier", day: 1789992000000, weighIns: [{ at: 1789819200000, kg: 71 }, { at: 1790164800000, kg: 73 }], kg: 71 },
  { name: "a zero is not a weigh-in", day: 1789992000000, weighIns: [{ at: 1789992000000, kg: 0 }, { at: 1790337600000, kg: 76 }], kg: 76 },
];
